import * as tus from "tus-js-client"

interface TusReadyPayload {
  upload_url: string
  signature: string
  expires: number
  video_id: string
  library_id: string | number
}

type LiveViewHook = {
  el: HTMLElement
  handleEvent(event: string, callback: (payload: unknown) => void): void
  pushEvent(event: string, payload: unknown): void
  _upload?: tus.Upload | null
  _file?: File | null
}

export const BunnyTusUpload: object = {
  mounted(this: LiveViewHook) {
    this._upload = null
    this._file = null

    // Cache the File reference when the user picks one.
    // We do this here rather than in handleEvent("bunny:tus_ready") because
    // the wizard navigates from step 4 to step 5 before the server pushes the
    // tus_ready event — the input element is still in the DOM (this hook
    // persists outside step conditionals) but reading files[] inside the event
    // callback would race with any LiveView DOM patch.
    const input = this.el.querySelector(
      "input[type='file']",
    ) as HTMLInputElement | null

    if (input) {
      input.addEventListener("change", () => {
        this._file = input.files?.[0] ?? null
        if (this._file) {
          this.pushEvent("bunny:file_selected", {
            name: this._file.name,
            size: this._file.size,
          })
        }
      })
    }

    this.handleEvent("bunny:tus_ready", (payload: unknown) => {
      const { upload_url, signature, expires, video_id, library_id } =
        payload as TusReadyPayload

      const file = this._file

      if (!file) {
        this.pushEvent("bunny:upload_error", { error: "No file selected" })
        return
      }

      const upload = new tus.Upload(file, {
        // `endpoint` (not `uploadUrl`) — tus POSTs here first to create the
        // upload slot, gets back a Location URL, then PATCHes chunks to it.
        // `uploadUrl` skips the POST and would silently send chunks to the
        // wrong URL, appearing to succeed but uploading nothing.
        endpoint: upload_url,
        chunkSize: 5 * 1024 * 1024,
        retryDelays: [0, 3000, 5000, 10000, 30000],
        headers: {
          AuthorizationSignature: signature,
          AuthorizationExpire: String(expires),
          VideoId: video_id,
          LibraryId: String(library_id),
        },
        metadata: {
          filetype: file.type,
          title: file.name,
        },
        onError: (error) => {
          this.pushEvent("bunny:upload_error", { error: error.message })
        },
        onProgress: (bytesUploaded: number, bytesTotal: number) => {
          const pct = Math.round((bytesUploaded / bytesTotal) * 100)
          this.pushEvent("bunny:upload_progress", { pct })
        },
        onSuccess: () => {
          this.pushEvent("bunny:upload_complete", {})
        },
      })

      upload.start()
      this._upload = upload
    })
  },

  destroyed(this: LiveViewHook) {
    if (this._upload) {
      this._upload.abort()
      this._upload = null
    }
  },
}
